/* eslint-disable @typescript-eslint/no-unused-vars */

'use client'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import { Box, Divider } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { InputData } from '@models'
import { Button, CommonInput, CommonInputData } from '@components/common'
import { CommonAutocomplete } from '@components/common/FormComponent/AutoComplete'
import { PopOverSection } from '@components/common/PopOverSection'
import { ROUTES } from '@utils/api/routes'
import { CustomStyles, getStyles } from '@utils/styles'
import { useQuery } from 'src/hooks/use-query'
import { QueryAllCentersData } from 'src/models/query.models'
import defaultStyle, { StylesClassNames } from './styles'

export type LocateNearCentersData = {
  heading: string
  inputBarProps: CommonInputData
  states: InputData[]
  cities?: InputData[]
  localities: InputData[]
}
type Props = LocateNearCentersData & {
  customStyles?: CustomStyles<StylesClassNames>
}
export type LocateNearCentersFilter = {
  city: string
  state: string
  locality: string
  search: string
}

export function LocateNearCenters({
  customStyles,
  cities,
  heading,
  localities,
  states,
  inputBarProps,
}: Props) {
  const router = useRouter()
  const styles = getStyles(defaultStyle, customStyles)
  const form = useForm<LocateNearCentersFilter>({
    mode: 'onTouched',
  })
  const { handleSubmit, setValue, watch } = form
  const selectedState = watch('state')
  const selectedCity = watch('city')

  const onSubmit = (filters: LocateNearCentersFilter): void => {
    const filteredFilters = Object.fromEntries(
      Object.entries(filters).filter(([key, value]) => value)
    )
    if (filteredFilters.state) {
      filteredFilters.state =
        states.find((state) => state.value === filteredFilters.state)?.label ||
        filteredFilters.state
    }
    // The location-master and centre-master use different state IDs. Once a
    // city is selected, it is the reliable centre filter.
    if (filteredFilters.city) delete filteredFilters.state
    const params = new URLSearchParams(filteredFilters)
    router.push(`/find-nearest-centre?${params.toString()}`)
  }
  const [currentCities, setCurrentCities] = useState<InputData[]>([])
  const [currentLocalities, setCurrentLocalities] = useState<InputData[]>([])
  const { queryCall: queryCities } = useQuery({
    method: 'get', // minLength: { value: 5, message: 'Min-Length should be 5' },
    url: ROUTES.NearestCentre,
    options: {
      onSuccess: (data: QueryAllCentersData) => {
        const cityMap = new Map<string, string>()
        data.data.rows.forEach((centre) => {
          const city = centre.city.trim().replace(/\s+/g, ' ')
          const normalizedCity = city.toLowerCase()
          if (city && normalizedCity && !cityMap.has(normalizedCity)) {
            cityMap.set(normalizedCity, city)
          }
        })
        setCurrentCities(
          Array.from(cityMap.values())
            .sort((first, second) => first.localeCompare(second))
            .map((city) => ({ label: city, value: city }))
        )
      },
    },
  })
  const { queryCall: queryLocalities } = useQuery({
    method: 'get',
    url: ROUTES.NearestCentre,
    options: {
      onSuccess: (data: QueryAllCentersData) => {
        const localityMap = new Map<string, string>()
        data.data.rows.forEach((centre) => {
          const locality = centre.locality.trim().replace(/\s+/g, ' ')
          const normalizedLocality = locality.toLowerCase()
          if (
            locality &&
            normalizedLocality !== 'select' &&
            !normalizedLocality.includes('no data found') &&
            !/^[-]+$/.test(locality)
          ) {
            localityMap.set(normalizedLocality, locality)
          }
        })
        const uniqueLocalities = Array.from(localityMap.values()).sort(
          (first, second) => first.localeCompare(second)
        )
        setCurrentLocalities(
          uniqueLocalities.map((locality) => ({
            label: locality,
            value: locality,
          }))
        )
      },
    },
  })

  useEffect(() => {
    setValue('city', '')
    setValue('locality', '')
    setCurrentLocalities([])
    if (selectedState) {
      const stateName = states.find(
        (state) => state.value === selectedState
      )?.label
      void queryCities({
        state: stateName || selectedState,
        limit: 1000,
      })
    } else {
      setCurrentCities([])
    }
  }, [selectedState])

  useEffect(() => {
    setValue('locality', '')
    if (selectedCity) {
      void queryLocalities({ city: selectedCity, limit: 1000 })
    } else {
      setCurrentLocalities([])
    }
  }, [selectedCity])

  return (
    <FormProvider {...form}>
      <PopOverSection
        heading={heading}
        customStyles={{
          popOverSection: styles('popOverSection').sx,
        }}
      >
        <Box
          component="form"
          autoComplete="off"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Box {...styles('wrapper')}>
            <CommonInput
              {...inputBarProps}
              customStyles={{ wrapper: styles('inputBar').sx }}
            />
            <Divider {...styles('divider')}>or</Divider>
            <Box {...styles('selectWrapper')}>
              <CommonAutocomplete
                name="state"
                options={states}
                placeholder="Select State"
                autoComplete={false}
                customStyles={{ wrapper: styles('select').sx }}
                // rules={{ required: 'State is required' }}
              />
              <CommonAutocomplete
                name="city"
                options={currentCities}
                placeholder="Select City"
                autoComplete={false}
                customStyles={{ wrapper: styles('select').sx }}
                // rules={{ required: 'City is required' }}
              />
              <CommonAutocomplete
                name="locality"
                options={currentLocalities}
                placeholder="Select Locality"
                disabled={!selectedCity}
                autoComplete={false}
                customStyles={{ wrapper: styles('select').sx }}
                // rules={{ required: 'State is required' }}
              />
            </Box>
            <Box {...styles('buttonWrapper')}>
              <Button
                label="Search"
                variant="contained"
                customStyles={{ button: styles('button').sx }}
                type="submit"
              />
            </Box>
          </Box>
        </Box>
      </PopOverSection>
    </FormProvider>
  )
}
